import './CreatorSetAttributes.scss';
import { useMemo } from 'react';
import { MdArrowForward } from 'react-icons/md';
import { YouTubeCreatorSetMember } from '@blisspointmedia/bpm-types/dist/pgTables/YoutubeCreatorSetMember';
import { Button } from '@Components/Button';
import { Size } from '@Components/designConstants';
import { InfoTooltip } from '@Components/InfoTooltip';
import { CopyButton } from '@Components/CopyButton';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { PAGE_TITLE } from '../youtube-planning-constants';
import { BuildRecommendationPrefill } from '../youtube-planning-types';
import {
  CopyableRow,
  OTHER_INTERESTS_TOOLTIP,
  USE_IN_BUILD_ACTION,
  Weighting
} from './creator-sets-constants';
import {
  aggregateCreatorSetTargeting,
  AttributeShare,
  formatShares,
  toBuildRecommendationPrefill,
  toCopyableRows,
  toTsv
} from './creator-set-attributes-utils';

const COPY_ALL = 'all';

interface CreatorSetAttributesProps {
  members: YouTubeCreatorSetMember[];
  // Opens Build Recommendation with the suggested values filled in. The button is hidden without it.
  onUseInBuild?: (prefill: BuildRecommendationPrefill) => void;
}

// The set's audience in Build Recommendation's own field names and values, so a planner can fill
// Audience Interest, Gender and Age Range in one step, or copy them across by hand. Other interests,
// devices and countries are reference only: the form has no field for devices or countries, and its
// Audience Interest search doesn't include affinity or in-market interests.
export const CreatorSetAttributes = ({ members, onUseInBuild }: CreatorSetAttributesProps) => {
  const targeting = useMemo(() => aggregateCreatorSetTargeting(members), [members]);
  const rows = useMemo(() => toCopyableRows(targeting), [targeting]);
  const copyable = rows.filter((row): row is CopyableRow & { value: string } => Boolean(row.value));

  if (targeting.memberCount === 0) return undefined;

  const trackCopy = (field: string) =>
    Mixpanel.track(MxE.ActionTriggered, { page: PAGE_TITLE, action: `copy ${field}` });

  const allText = toTsv(copyable);

  const fillBuildRecommendation = onUseInBuild
    ? () => {
        onUseInBuild(toBuildRecommendationPrefill(targeting));
        Mixpanel.track(MxE.ActionTriggered, { page: PAGE_TITLE, action: USE_IN_BUILD_ACTION });
      }
    : undefined;

  return (
    <section className='creator-set-attributes' data-testid='creator-set-attributes'>
      <div className='creator-set-attributes-header'>
        <div>
          <div className='creator-set-attributes-title'>Build Recommendation inputs</div>
          <div className='creator-set-attributes-coverage'>
            {targeting.membersWithAudience === 0
              ? 'None of these creators share viewer data with Google, so there’s nothing to suggest.'
              : `Based on ${targeting.membersWithAudience} of ${conditionallyPluralizeWithCount(targeting.memberCount, 'creator')} (those who share viewer data), ${targeting.weighting === Weighting.SUBSCRIBERS ? 'weighted by subscribers' : 'weighted equally'}.`}
          </div>
        </div>
        {copyable.length > 0 && (
          <HeaderActions
            allText={allText}
            onCopyAll={() => trackCopy(COPY_ALL)}
            onUseInBuild={fillBuildRecommendation}
          />
        )}
      </div>
      {targeting.membersWithAudience > 0 && (
        <div className='creator-set-attributes-rows'>
          {rows.map(row => (
            <AttributeRow
              key={row.label}
              row={row}
              onCopy={() => trackCopy(row.label.toLowerCase())}
            />
          ))}
          <ReferenceRow
            label='Other interests'
            shares={targeting.unavailableInterests}
            tooltip={OTHER_INTERESTS_TOOLTIP}
          />
          <ReferenceRow label='Devices' shares={targeting.devices} />
          <ReferenceRow label='Countries' shares={targeting.countries} />
        </div>
      )}
    </section>
  );
};

interface HeaderActionsProps {
  allText: string;
  onCopyAll: () => void;
  onUseInBuild?: () => void;
}

const HeaderActions = ({ allText, onCopyAll, onUseInBuild }: HeaderActionsProps) => (
  <div className='creator-set-attributes-actions'>
    <CopyButton label='Copy all' text={allText} onCopied={onCopyAll} />
    {onUseInBuild && (
      <Button size={Size.SM} trailingElement={<MdArrowForward />} onClick={onUseInBuild}>
        Use in Build Recommendation
      </Button>
    )}
  </div>
);

interface AttributeRowProps {
  row: CopyableRow;
  onCopy: () => void;
}

const AttributeRow = ({ row: { label, value, note }, onCopy }: AttributeRowProps) => (
  <div className='creator-set-attribute' data-testid={`attribute-${label}`}>
    <span className='creator-set-attribute-label'>{label}</span>
    <span className='creator-set-attribute-value'>
      {value ?? '—'}
      {value && <CopyButton text={value} aria-label={`Copy ${label}`} onCopied={onCopy} />}
    </span>
    {note && <span className='creator-set-attribute-note'>{note}</span>}
  </div>
);

interface ReferenceRowProps {
  label: string;
  shares: AttributeShare[];
  tooltip?: string;
}

const ReferenceRow = ({ label, shares, tooltip }: ReferenceRowProps) => (
  <div className='creator-set-attribute reference'>
    <span className='creator-set-attribute-label'>
      {label}
      {tooltip && <InfoTooltip size={Size.SM}>{tooltip}</InfoTooltip>}
    </span>
    <span className='creator-set-attribute-value'>
      {shares.length > 0 ? formatShares(shares) : '—'}
    </span>
  </div>
);
